import React, { useEffect, useState } from 'react';
import { buildingsApi } from '../services/api';
import type { BuildingProtocolEntry } from '../services/api';

/**
 * Migr. 078 (spotkanie z Bronkiem 21.10): „Przy planowaniu budynku chcę mieć możliwość wyboru, z którego roku dane
 * z protokołu mają być importowane”. Pole „Dane z protokołu CEEB” w oknach „Zaplanuj przegląd” (lista protokołów
 * budynku z lustra, GET /buildings/:id/protocols) i „Zaplanuj dla zaznaczonych” (rok — protokół z tego roku wybiera
 * serwer per budynek).
 *
 * Wartość '' = domyślnie: protokół BIEŻĄCY budynku (najnowszy protokół adresu w CEEB — to, co import brał zawsze).
 * Wybór steruje wyłącznie importem danych do przeglądu; bot dalej pisze do protokołu tego roku.
 */

export const PREFILL_SOURCE_DEFAULT = '';

/** Opcje okna pojedynczego: protokoły z lustra bez usuniętych w CEEB (backend i tak je odrzuca), od najnowszych. */
export function prefillSourceOptions(protocols: BuildingProtocolEntry[]): { value: string; label: string }[] {
  return protocols
    .filter((p) => !p.deletedInCeebAt)
    .map((p) => ({
      value: p.ceebId,
      label: `${p.year ?? '—'} · ${p.ceebId}${p.ceebStatus ? ` (${p.ceebStatus})` : ''}${p.isCurrent ? ' — bieżący' : ''}`,
    }));
}

/** Etykieta opcji domyślnej: „Bieżący protokół — 445159/2026 (Wersja robocza)” albo bez numeru, gdy lustro go nie zna. */
export function defaultSourceLabel(protocols: BuildingProtocolEntry[]): string {
  const cur = protocols.find((p) => p.isCurrent && !p.deletedInCeebAt);
  if (!cur) return 'Bieżący protokół (domyślnie)';
  return `Bieżący protokół (domyślnie) — ${cur.ceebId}${cur.ceebStatus ? ` (${cur.ceebStatus})` : ''}`;
}

/** Lata do wyboru w oknie zbiorczym: bieżący i dwa poprzednie. */
export function bulkSourceYears(now: Date = new Date()): number[] {
  const y = now.getFullYear();
  return [y, y - 1, y - 2];
}

const selectStyle: React.CSSProperties = {
  width: '100%',
  minHeight: 'var(--touch-min)',
  padding: '0 var(--space-3)',
  boxSizing: 'border-box',
  fontFamily: 'var(--font-sans)',
  fontSize: 'var(--text-body)',
  color: 'var(--text-primary)',
  background: 'var(--surface-card)',
  border: 'var(--border-width-strong) solid var(--border-strong)',
  borderRadius: 'var(--radius-md)',
};
const caption: React.CSSProperties = { fontSize: 'var(--text-caption)', color: 'var(--text-secondary)' };
const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-label)', fontWeight: 'var(--weight-semibold)', color: 'var(--text-secondary)',
};

const WHAT_LOADS = 'Z wybranego protokołu import wczyta: zakres kontroli (liczby przewodów), „Ustalenia”, '
  + 'niewykonane zalecenia i uwagi (tylko w puste pola przeglądu), lokale z ich powierzchnią i źródłami ciepła '
  + 'oraz dane budynku — przy najbliższym imporcie z CEEB (nocny albo „Odśwież z CEEB”). Zapisy z terenu '
  + 'nie są nadpisywane, a wysyłka i tak idzie do protokołu bieżącego roku.';

export interface PlanPrefillSourceFieldProps {
  idPrefix: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  /** Okno pojedyncze: budynek, którego protokoły pokazać. */
  buildingId?: string;
  /** Okno zbiorcze: zamiast protokołów — rok. */
  bulk?: boolean;
}

export default function PlanPrefillSourceField({
  idPrefix, value, onChange, disabled, buildingId, bulk,
}: PlanPrefillSourceFieldProps) {
  const [protocols, setProtocols] = useState<BuildingProtocolEntry[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (bulk || !buildingId) return undefined;
    let alive = true;
    setProtocols(null);
    setLoadError(null);
    // Promise.resolve().then: także synchroniczny wyjątek klienta API kończy się komunikatem przy polu, nie awarią okna.
    Promise.resolve().then(() => buildingsApi.getProtocols(buildingId))
      .then((rows) => { if (alive) setProtocols(rows); })
      .catch((e: any) => { if (alive) setLoadError(e?.message || 'Nie udało się wczytać protokołów budynku'); });
    return () => { alive = false; };
  }, [buildingId, bulk]);

  const selectId = `${idPrefix}-prefill-source`;
  const options = protocols ? prefillSourceOptions(protocols) : [];
  return (
    <div data-testid={selectId} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', fontFamily: 'var(--font-sans)' }}>
      <label htmlFor={`${selectId}-select`} style={labelStyle}>Dane z protokołu CEEB</label>
      {bulk ? (
        <select id={`${selectId}-select`} value={value} disabled={disabled} onChange={(e) => onChange(e.target.value)} style={selectStyle}>
          <option value={PREFILL_SOURCE_DEFAULT}>Bieżący protokół budynku (domyślnie)</option>
          {bulkSourceYears().map((y) => <option key={y} value={String(y)}>Protokół z {y} r.</option>)}
        </select>
      ) : (
        <select
          id={`${selectId}-select`}
          value={value}
          disabled={disabled || protocols === null}
          onChange={(e) => onChange(e.target.value)}
          style={selectStyle}
        >
          <option value={PREFILL_SOURCE_DEFAULT}>{protocols ? defaultSourceLabel(protocols) : 'Bieżący protokół (domyślnie)'}</option>
          {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {!bulk && protocols === null && !loadError && <span style={caption}>Wczytuję protokoły budynku…</span>}
      {loadError && (
        <span role="alert" style={{ ...caption, color: 'var(--status-deny)' }}>
          {loadError} — przegląd zaplanujesz z danymi z protokołu bieżącego.
        </span>
      )}
      {!bulk && protocols !== null && options.length === 0 && (
        <span style={caption}>Budynek nie ma jeszcze protokołów w lustrze CEEB.</span>
      )}
      {bulk && value && (
        <span style={caption}>Budynek bez protokołu z {value} r. dostanie dane z protokołu bieżącego — pokażemy to w wyniku.</span>
      )}
      {value && <span data-testid={`${selectId}-hint`} style={caption}>{WHAT_LOADS}</span>}
    </div>
  );
}
